import { StoreExt } from '@utils/reactExt'
import { Schema$UserComment, Schema$UserCommentExt } from '@interface/comment'
import { ATTACHMENT_TYPE, USER_COMMENT_TYPE } from '@v2/constants/common'
import _ from 'lodash'
import { authStore, globalStore } from '@store/index'
import { action, observable, runInAction } from 'mobx'

export class CommentStore extends StoreExt {

  isCreateCommentLoading: boolean

  isUpdateCommentLoading: boolean

  isCommentLikeLoading: boolean

  @observable
  commentMap = {
    [USER_COMMENT_TYPE.USER_PICK_COMMENT]: {
      comments: [] as Schema$UserCommentExt[],
      hasMore: false,
      offset: 0,
    },
    [USER_COMMENT_TYPE.COMPANY_PICK_COMMENT]: {
      comments: [] as Schema$UserCommentExt[],
      hasMore: false,
      offset: 0,
    },
  }

  // @observable
  // comments: Schema$UserCommentExt[] = []
  //
  // @observable
  // commentHasMore = false
  //
  // @observable
  // commentOffset = 0

  @observable
  myComments: Schema$UserComment[] = []

  @observable
  myCommentHasMore = false

  @observable
  myCommentOffset = 0

  @observable
  commentTopEditorFocus = false

  validateContentComment = (data: Schema$UserComment) => {
    const { content, attachmentType } = data
    if (attachmentType !== ATTACHMENT_TYPE.TEXT_ONLY) {
      return true
    }
    if (_.size(_.trim(content)) < 1) {
      console.warn('댓글 입력이 없습니다.')
      return false
    }

    if (content.replace(/\n/gi, '').length > 3000) {
      console.warn('댓글이 3000자를 넘었습니다.')
      return false
    }

    if (_.split(content, '\n').length > 300) {
      console.warn('300줄을 초과하였습니다.')
      return false
    }

    return authStore.isSignedIn
  }

  createComment = async (data: Schema$UserComment): Promise<Schema$UserComment> => {
    if (this.isCreateCommentLoading) {
      return null
    }

    if (!this.validateContentComment(data)) {
      return null
    }

    let createdComment: Schema$UserComment
    try {
      this.isCreateCommentLoading = true

      const params = { comment: data, isNew: true }
      const result = await this.api.comment.saveComment(params)
      createdComment = result.comment
      runInAction(() => {
        globalStore.addActivityScoreNoticeQueue(result.activityInfo)
      })
    } catch (err) {
      console.error(err)
      this.isCreateCommentLoading = false
      return null
    }
    this.isCreateCommentLoading = false
    return createdComment
  }

  updateComment = async (comment: Schema$UserComment): Promise<Schema$UserComment> => {
    if (this.isUpdateCommentLoading) {
      return null
    }

    if (!this.validateContentComment(comment)) {
      return null
    }

    let updatedComment: Schema$UserComment
    try {
      this.isUpdateCommentLoading = true

      const result = await this.api.comment.saveComment({ comment, isNew: false })
      updatedComment = result.comment
    } catch (err) {
      console.error(err)
      this.isUpdateCommentLoading = false
      return null
    }
    this.isUpdateCommentLoading = false
    return updatedComment
  }

  deleteComment = async (comment: Schema$UserComment): Promise<Schema$UserComment> => {
    if (!authStore.isSignedIn) {
      return null
    }

    if (comment.commenter.userPromiseId !== authStore.userInfo.id) {
      return null
    }

    let disabledComment
    try {
      const params = { comment }

      const result = await this.api.comment.deleteComment(params)
      disabledComment = result.comment
    } catch (err) {
      console.error(err)
      return null
    }

    return disabledComment
  }

  @action
  resetComments = (commentType: USER_COMMENT_TYPE): void => {
    this.commentMap[commentType] = {
      comments: [],
      hasMore: false,
      offset: 0,
    }
    this.commentTopEditorFocus = false
  }

  @action
  fetchComments = async (commentType: USER_COMMENT_TYPE, targetId: string, reset = false): Promise<void> => {
    try {
      const params = {
        commentType,
        targetId,
        offset: this.commentMap[commentType].offset,
      }
      const result = await this.api.comment.fetchComments(params)
      runInAction(() => {
        let newComments: Schema$UserCommentExt[] = []
        for (const item of result.items) {
          if (item.commentDepth > 0) {
            let rootComment = _.find(this.commentMap[commentType].comments, { id: item.rootCommentId })
            if (!rootComment) {
              rootComment = _.find(newComments, { id: item.rootCommentId })
            }
            if (rootComment) {
              rootComment.replies.push({ ...item, visibleEditor: false })
            }
          } else {
            newComments.push({ ...item, visibleEditor: false })
          }
        }
        if (!reset) {
          newComments = _.concat(this.commentMap[commentType].comments, newComments)
        }
        this.commentMap[commentType].comments = [].concat(newComments)
        this.commentMap[commentType].hasMore = result.hasMore
        this.commentMap[commentType].offset += 20
      })
    } catch (err) {
      console.error(err)
    }
  }

  @action
  createCommentHandler = (createdComment: Schema$UserComment): void => {
    const commentType = createdComment.commentType
    if (createdComment.commentDepth === 0) {
      const newCreatedComment: Schema$UserCommentExt = { ...createdComment, visibleEditor: false }
      this.commentMap[commentType].comments.unshift(newCreatedComment)
    } else {
      const rootComment = _.find(this.commentMap[commentType].comments, { id: createdComment.rootCommentId })
      if (rootComment) {
        if (_.isNil(rootComment.replies)) {
          rootComment.replies = []
        }
        const newCreatedComment = { ...createdComment, visibleEditor: false }
        rootComment.replies.push(newCreatedComment)
        rootComment.replyCount += 1
        this.commentMap[commentType].comments = [].concat(this.commentMap[commentType].comments)
      }
    }
  }

  @action
  updateCommentHandler = (updatedComment: Schema$UserComment): void => {
    const commentType = updatedComment.commentType
    if (updatedComment.commentDepth === 0) {
      const exists = _.find(this.commentMap[commentType].comments, { id: updatedComment.id })
      if (exists) {
        exists.content = updatedComment.content
        exists.filteredContent = updatedComment.filteredContent
        exists.attachmentType = updatedComment.attachmentType
        exists.attachment = updatedComment.attachment
        exists.attachmentDetail = updatedComment.attachmentDetail
        exists.lastModifiedAt = updatedComment.lastModifiedAt
      }
    } else {
      const rootComment = _.find(this.commentMap[commentType].comments, { id: updatedComment.rootCommentId })
      if (rootComment) {
        const exists = _.find(rootComment.replies, { id: updatedComment.id })
        if (exists) {
          exists.content = updatedComment.content
          exists.filteredContent = updatedComment.filteredContent
          exists.attachmentType = updatedComment.attachmentType
          exists.attachment = updatedComment.attachment
          exists.attachmentDetail = updatedComment.attachmentDetail
          exists.lastModifiedAt = updatedComment.lastModifiedAt
        }
      }
    }
  }

  @action
  deleteCommentHandler = (deletedComment: Schema$UserComment): void => {
    const commentType = deletedComment.commentType
    if (deletedComment.commentDepth === 0) {
      if (_.isEmpty(deletedComment.replies)) {
        this.commentMap[commentType].comments = _.filter(this.commentMap[commentType].comments, i => {
          return i.id !== deletedComment.id
        })
      } else {
        const exists = _.find(this.commentMap[commentType].comments, { id: deletedComment.id })
        if (exists) {
          exists.enabled = deletedComment.enabled
        }
      }
    } else {
      const rootComment = _.find(this.commentMap[commentType].comments, { id: deletedComment.rootCommentId })
      if (rootComment) {
        const exists = _.find(rootComment.replies, { id: deletedComment.id })
        if (exists) {
          _.remove(rootComment.replies, { id: deletedComment.id })
          rootComment.replyCount -= 1
          if (!rootComment.enabled && _.isEmpty(rootComment.replies)) {
            _.remove(this.commentMap[commentType].comments, { id: rootComment.id })
          }
          this.commentMap[commentType].comments = [].concat(this.commentMap[commentType].comments)
        }
      }
    }
  }

  @action
  toggleCommentLikeHandler = (updatedComment: Schema$UserComment): void => {
    const { isLike, id, rootCommentId, commentDepth, commentType } = updatedComment
    if (commentDepth === 0) {
      const existsItem = _.find(this.commentMap[commentType].comments, { id })
      if (existsItem) {
        existsItem.isLike = isLike
        if (isLike) {
          existsItem.likeCount += 1
        } else {
          existsItem.likeCount -= 1
        }
      }
    } else {
      const rootComment = _.find(this.commentMap[commentType].comments, { id: rootCommentId })
      if (rootComment && !_.isEmpty(rootComment.replies)) {
        const exists = _.find(rootComment.replies, { id })
        if (exists) {
          exists.isLike = isLike
          if (isLike) {
            exists.likeCount += 1
          } else {
            exists.likeCount -= 1
          }
        }
      }
    }
  }

  /**
   * 댓글 에디터를 열고 닫고 한다.
   * 열림 true 닫힘 false 를 반환 한다.
   * @param comment
   */
  @action
  toggleVisibleEditorHandler = (comment: Schema$UserCommentExt): boolean => {
    const commentType = comment.commentType
    // 에디터를 열려고 한다면.. 다른 댓글의 에디터를 모두 닫는다.
    if (!comment.visibleEditor) {
      for (const item of this.commentMap[commentType].comments) {
        if (item.visibleEditor) {
          item.visibleEditor = false
        }
        if (!_.isEmpty(item.replies)) {
          for (const reply of item.replies) {
            if (reply['visibleEditor']) {
              reply['visibleEditor'] = false
            }
          }
        }
      }
    }

    const isOpen = !comment.visibleEditor
    if (comment.commentDepth === 0) {
      const existsItem = _.find(this.commentMap[commentType].comments, { id: comment.id })
      if (existsItem) {
        existsItem.visibleEditor = !existsItem.visibleEditor
      }
    } else if (comment.commentDepth > 0) {
      const rootComment = _.find(this.commentMap[commentType].comments, { id: comment.rootCommentId })
      if (rootComment) {
        const existsItem = _.find(rootComment.replies, { id: comment.id })
        if (existsItem) {
          existsItem['visibleEditor'] = !existsItem['visibleEditor']
        }
      }
    }

    return isOpen
  }

  @action
  invisibleAllCommentEditor = (commentType: USER_COMMENT_TYPE): void => {
    for (const item of this.commentMap[commentType].comments) {
      if (item.visibleEditor) {
        item.visibleEditor = false
      }
      if (!_.isEmpty(item.replies)) {
        for (const reply of item.replies) {
          if (reply['visibleEditor']) {
            reply['visibleEditor'] = false
          }
        }
      }
    }
  }

  @action
  setCommentTopEditorFocus = (isFocus: boolean, commentType: USER_COMMENT_TYPE): void => {
    if (isFocus) {
      this.invisibleAllCommentEditor(commentType)
    }
    runInAction(() => {
      this.commentTopEditorFocus = isFocus
    })
  }

  @action
  fetchMyComments = async (commentType: USER_COMMENT_TYPE, reset = false, baseMonth: string): Promise<void> => {
    try {
      const params = {
        commentType,
        offset: this.myCommentOffset,
        baseMonth,
      }
      const result = await this.api.comment.fetchMyComments(params)

      runInAction(() => {
        let newComments = result.items
        if (!reset) {
          newComments = _.concat(this.myComments, newComments)
        }
        this.myComments = [].concat(newComments)
        this.myCommentHasMore = result.hasMore
        this.myCommentOffset += 20
      })
    } catch (err) {
      console.error(err)
    }
  }

  @action
  updateMyCommentHandler = (updatedComment: Schema$UserComment): void => {
    const exists = _.find(this.myComments, { id: updatedComment.id })
    if (exists) {
      exists.content = updatedComment.content
      exists.filteredContent = updatedComment.filteredContent
      exists.attachmentType = updatedComment.attachmentType
      exists.attachment = updatedComment.attachment
      exists.attachmentDetail = updatedComment.attachmentDetail
      exists.lastModifiedAt = updatedComment.lastModifiedAt
    }
  }

  @action
  deleteMyCommentHandler = (deletedComment: Schema$UserComment): Schema$UserComment[] => {
    const deletedItems = _.remove(this.myComments, { id: deletedComment.id })
    this.myComments = [].concat(this.myComments)
    return deletedItems
  }

  @action
  toggleMyCommentLikeHandler = (updatedComment: Schema$UserComment): void => {
    const { isLike, id } = updatedComment
    const existsItem = _.find(this.myComments, { id })
    if (existsItem) {
      existsItem.isLike = isLike
      if (isLike) {
        existsItem.likeCount += 1
      } else {
        existsItem.likeCount -= 1
      }
    }
  }

  @action
  resetMyComments = (): void => {
    this.myComments = []
    this.myCommentHasMore = false
    this.myCommentOffset = 0
  }

  @action
  async toggleCommentLike(comment: Schema$UserComment): Promise<Schema$UserComment> {
    if (this.isCommentLikeLoading) {
      return null
    }

    let updatedComment: Schema$UserComment
    try {
      this.isCommentLikeLoading = true
      const result = await this.api.comment.toggleCommentLike({ comment })
      updatedComment = result.comment
    } catch (err) {
      console.error(err)
      this.isCommentLikeLoading = false
      return null
    }
    this.isCommentLikeLoading = false
    return updatedComment
  }

  @action
  setCommentBlock(
    commentList: Schema$UserCommentExt[],
    userPromiseId: string,
    isUserBlock = false,
    isCommentBlock = false
  ) {
    _.forEach(commentList, comment => {
      if (!_.isEmpty(comment.replies)) {
        comment.replies = this.setCommentBlock(
          comment.replies,
          userPromiseId,
          isUserBlock,
          isCommentBlock
        )
      }

      if (comment.commenter.userPromiseId === userPromiseId) {
        comment.commenter.isBlocked = isUserBlock
        comment.isBlocked = isCommentBlock
      }
    })

    return _.concat([], commentList)
  }
}

export default new CommentStore()
